export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  lastModified?: string; // 最后修改时间（可选）
  description: string;
  tags: string[];
  image: string;
  content: string;
  excerpt: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: "truesize-world-overview",
    title: "TrueSize.World : A Better Way to Compare True Size of Region",
    slug: "truesize-world-overview",
    date: "2025-09-22",
    lastModified: "2025-09-30",
    description: "A privacy-friendly true size map for the real size of countries, continents, oceans, islands, lakes and cities. Import or draw GeoJSON locally and export results in one click.",
    tags: ["the true size of countries",  "the true size of lakes", "the true size of oceans", "the true size of islands", "the true size of cities", "the true size of continents"],
    image: "/images/blog/overview-hero.webp",
    excerpt: "TrueSize.World is the true size map focused on showing the real scale of countries, continents, oceans, cities ,lakes, islands, and cities-on Interactive Maps",
    content: `> TL;DR — TrueSize.World is a **true size map** focused on showing the **real size** of places—**countries, continents, oceans, lakes, islands, and cities**—on one canvas. It highlights the perception gap you get on common web maps and makes comparisons fast, privacy-friendly, and easy to share.

## What makes TrueSize.World different?

- **Beyond countries**  
  Most "the true scale of …" tools stop at countries. We go further: compare the **real scale of countries** alongside **continents, oceans, lakes, islands, and cities** on the same interactive stage.

- **Local, privacy-friendly workflow**  
  Import your **GeoJSON** and **draw** shapes **in your browser**; nothing is uploaded. It's perfect for sensitive overlays in class, newsroom, or research.

- **Fine control for clarity**  
  Use **opacity** and **rotation** to make overlays readable and orientations comparable. Switch **basemaps** (standard / imagery / terrain) for context.

- **Live latitude & exaggeration**  
  Web Mercator preserves angles but inflates areas away from the equator. We surface a **live center-latitude readout** and an **exaggeration ratio** so viewers grasp why "map dimensions" often differs from **the real area**.

- **Share & export**  
  Share link to reopen the exact **true scale map** state. Export **GeoJSON** for slides, docs, or further GIS work.

![Compare the true size Interactive Comparison Map at https://truesize.world](/images/blog/overview-collage.jpg)

## What you can compare today

- **Countries** — e.g., *China vs United States*, *India vs Australia*  
- **Continents** — e.g., *Europe vs Australia*  
- **Oceans** — e.g., *Pacific vs Atlantic*  
- **Lakes** — e.g., *Lake Superior vs Lake Victoria*  
- **Islands** — e.g., *Borneo vs New Guinea*  
- **Cities** — e.g., *London vs New York*, *Shanghai vs Tokyo*, *Singapore vs Hong Kong*

👉 **Try our Interactive Map**: [Open TrueSize.World](/map)

## Why the "real map size" matters

If you've ever wondered why **Greenland** looks huge next to **Africa** on a classroom map, that's projection distortion talking. A **true scale map** makes it obvious: drag shapes to similar latitudes, rotate, and adjust opacity. The **real size** emerges immediately.`
  },
  {
    id: "how-to-compare-true-size-map",
    title: "How to Compare the True Size of Countries and Regions",
    slug: "how-to-compare-real-size-map",
    date: "2025-09-22",
    lastModified: "2025-09-30",
    description: "Step-by-step guide to compare the real size of countries, continents, oceans, lakes, islands, and cities. Learn opacity, rotation, basemap switch, and local GeoJSON import.",
    tags: ["how-to", "Interactive Comparison Map", "truesize.world"],
    image: "/images/blog/howto-hero.webp",
    excerpt: "A guide to exploring true size maps-compare countries, continents, oceans, lakes, islands, and cities at TrueSize.World for clearer geographic insights.",
    content: `A quick **how-to** for your next lesson, explainer, or report—covering **countries, continents, oceans, lakes, islands, and cities** on our **true size map**.

## 1) Start from Home or Map

Use the search on the homepage (supports names in six languages) to pick one or two entities.  
- **One entity** → go to its entity page.  
- **Two entities** → open a **compare page** or click **Open in Map** to jump straight into the interactive **real size map**.

![Search and compare interface on TrueSize.World](/images/blog/Search-a-vs-b.jpg)

## 2) Add layers and move them

Inside **Interactive Comparison Page**, use search to **add layers**. Drag shapes; the **center latitude** and **exaggeration ratio** update live—handy when explaining why "map size" differs from **the real size**.

## 3) Make overlays readable

- **Opacity**: see both outlines at once.  
- **Rotation**: align coastlines or borders; snap assists help reproducibility.

## 4) Switch basemaps

Choose **standard**, **imagery**, or **terrain** to match your story—coastlines, mountains, urban texture, and more.

![Compare true size on imagery map at TrueSize.World](/images/blog/compare-true-size-on imagery-map.jpg)

## 5) Bring your own data—locally

Click **Import** to load **GeoJSON**; or **Draw** polygons. Everything stays in your browser. Great for highlighting regions while comparing **the true size of lakes**, **the true size of oceans**, or **the true size of cities**.

## 6) Export & share

Export **GeoJSON**. Copy a **share link** to reopen the exact state in **Interactive Comparison Map**.

**Shortcuts**  
- Type "A vs B" or "A, B" to auto-parse two entities.  
- \`/\` or \`Cmd/Ctrl+K\` to focus search; \`Enter\` to select.`
  },
  {
    id: "methods-true-size-map",
    title: "How Our True Size Map Works: Methods & Calculations",
    slug: "methods-true-size-map",
    date: "2025-09-21",
    lastModified: "2025-09-30",
    description: "From Mercator scale by latitude (exaggeration ratio) to overlays, basemaps, and local exports—here's the math and logic behind our real size map.",
    tags: ["Mercator", "projection", "exaggeration"],
    image: "/images/blog/methods-hero.webp",
    excerpt: "Explore the methods behind TrueSize.World: projection math and mapping algorithms that reveal why map size differs from reality and show true scale.",
    content: `This note explains the core ideas behind our **true size map**—why "map size" often differs from **the real size**, and how TrueSize.World exposes that gap without uploading any data.

## Data & geometry

We use public boundary and coastline datasets optimized for web interaction (topology simplification, low-noise outlines). Precision cadastral work is out of scope; our focus is **education and visualization**.

## Exaggeration by latitude (Web Mercator)

Many web maps use a Mercator-based projection. It preserves angles but **inflates areas** with latitude. A simple model of the **linear scale factor** is:

k(φ) = sec φ

- At **φ = 60°**, \`k ≈ 2.0\`  
- At **φ = 80°**, \`k ≈ 5.8\`

We display a **live center-latitude** and an **exaggeration ratio** so users can relate what they see to a quantitative factor. When you drag **Greenland** toward the equator, its apparent **map size** converges toward its **real size**.

## Overlays: opacity, rotation, alignment

- **Opacity** lets you see two outlines simultaneously.  
- **Rotation** (with optional snaps) lets you align orientations and edges for apples-to-apples comparisons—great for **the true size of islands** or **the real size of countries** with similar silhouettes.

## Basemaps & rendering

We provide **standard**, **imagery**, and **terrain** basemaps to fit different storytelling needs while keeping the interaction smooth.

## Privacy & exports

Imports (GeoJSON) and drawings run **locally** in your browser. Exports—**GeoJSON**—are generated client-side. That's ideal for sensitive or unpublished geometries.

**See also**: [Interactive Map](/map)`
  },
  {
    id: "why-greenland-looks-so-big",
    title: "Why Greenland Looks So Big: A Quick Guide to Map Distortion",
    slug: "why-greenland-looks-so-big",
    date: "2025-09-19",
    description: "Greenland looks gigantic on many maps, but Africa is far larger. Learn how projection distortion works and compare the true size of Greenland on our interactive true size map.",
    tags: ["explainer", "true size map", "real size map", "Greenland", "projection", "the true size of countries"],
    image: "/images/blog/greenland-hero.webp",
    excerpt: "Greenland appears Africa-area on maps, yet Africa dwarfs it. The reason: projection distortion, with Mercator maps enlarging areas far from the equator.",
    content: `On many maps, **Greenland** looks as big as **Africa**. In reality, Africa dwarfs it. The culprit is **projection distortion**—especially on Mercator-family maps that expand areas away from the equator.

## What Mercator preserves—and what it stretches

Mercator preserves angles (great for navigation) but **blows up area** toward the poles. By **60°N**, scale is roughly **2×**; by **80°N**, nearly **6×**. Because Greenland sits at high latitudes, its "map size" feels much larger than its **real size**.

![Greenland vs Africa overlay captured from https://truesize.world](/images/blog/greenland-vs-africa.jpg)

## See it on a true size map

Open our [Interactive Map](/map) and add **Greenland** and **Africa**. Drag, rotate, and adjust opacity. Watch the **center-latitude** and **exaggeration ratio**—a live reminder that **real size** and "map size" are not the same.

More pairs to try:  
- *Greenland vs Australia*  
- *Europe vs Australia*  
- *Borneo vs New Guinea* (islands)  
- *Pacific Ocean vs Atlantic Ocean* (oceans)  
- *London vs New York*, *Shanghai vs Tokyo*, *Singapore vs Hong Kong* (cities)

## Takeaway

No single projection is perfect. A **true size map** with interactive overlays makes the trade-offs tangible—so anyone can see the **real size of countries**, the **real size of oceans and lakes**, the **real size of islands**, and the **true size of cities**.`
  },
  {
    id: "map-projections-at-a-glance",
    title: "Visual Comparison of Map Projections: Mercator and more",
    slug: "map-projections-at-a-glance",
    date: "2025-09-18",
    description: "A quick tour of common world map projections and how they affect the true size of places—Mercator, Equal Earth, Robinson, Gall–Peters, Mollweide, Winkel Tripel, Lambert Azimuthal Equal-Area, Plate Carrée, and Sinusoidal.",
    tags: ["projections", "true size map", "real size map", "the true size of countries", "distortion"],
    image: "/images/blog/projections-hero.webp",
    excerpt: "This gallery reveals how projections alter area, shape, distance, and direction—explaining why the true size of places can differ from map views.",
    content: `> This gallery explains how different projections treat **area, shape, distance, and direction**—and why the **real size** of places can diverge from their **map size**. Explore a large gallery at <a href="https://map-projections.net/singleview.php" rel="nofollow" target="_blank">map-projections</a>.

## 1) Mercator (conformal)

- **Keeps** local shape/angles; familiar online (Web Mercator).  
- **Distorts** area heavily at high latitudes (the "Greenland effect").  
- **Best for**: bearings, navigation, and web map context.  
- **Mind the trade-offs**: not ideal when communicating *the true size of countries*.

![Mercator projection example, illustrating area growth with latitude on https://truesize.world](/images/blog/mercator.jpg)

## 2) Equal Earth (equal-area)

- **Keeps** global areas fair—great for **real size of countries**.  
- **Distorts** shapes somewhat; visually balanced.  
- **Best for**: world thematic maps where area comparability matters.

![Equal Earth projection preserves the real size of countries, from https://truesize.world](/images/blog/equal-earth.jpg)

## 3) Robinson (compromise)

- **Balances** overall look without being equal-area or conformal.  
- **Distorts** edges/poles more, center less.  
- **Best for**: attractive world maps and atlases.

![Robinson projection for balanced world maps, previewed on https://truesize.world](/images/blog/robinson.jpg)

## 4) Gall–Peters (equal-area)

- **Keeps** area; **stretches** shapes vertically near the equator.  
- **Best for**: discussions about fairness in **the true size of countries**.

![Gall–Peters equal-area view highlighting fair area on https://truesize.world](/images/blog/gall-peters.jpg)

## 5) Mollweide (equal-area)

- **Keeps** area with an oval world outline; shapes are rounded.  
- **Best for**: global population, climate, and thematic cartograms.

![Mollweide equal-area oval world outline, explained on https://truesize.world](/images/blog/mollweide.jpg)

## 6) Winkel Tripel (compromise)

- **Balances** area, shape, and distance; adopted by National Geographic.  
- **Best for**: general-purpose world maps where no single metric dominates.

![Winkel Tripel compromise projection, balanced appearance noted on https://truesize.world](/images/blog/winkel-tripel.jpg)

## 7) Lambert Azimuthal Equal-Area (equal-area, azimuthal)

- **Keeps** area, **true direction** from center; great for hemispheres.  
- **Best for**: polar or continental focuses where area matters.

![Lambert Azimuthal Equal-Area for hemispheric views, annotated on https://truesize.world](/images/blog/lambert-azimuthal-equal-area.jpg)

## 8) Plate Carrée / Equirectangular (cylindrical, simple)

- **Keeps** straight lat/long grid; very simple to compute.  
- **Distorts** shapes and areas strongly toward poles.  
- **Best for**: quick raster data display, technical debugging.

![Plate Carrée (equirectangular) with straight graticule, example on https://truesize.world](/images/blog/plate-carree.jpg)

## 9) Sinusoidal (equal-area)

- **Keeps** area; meridians are sinusoidal curves.  
- **Best for**: global thematic rasters (e.g., land-cover).

![Sinusoidal equal-area projection with curved meridians, showcased at https://truesize.world](/images/blog/sinusoidal.jpg)

## Which projection should I use?

- If you need **fair area comparisons**—*the true size of countries*, or the **real size** of oceans/lakes/islands/cities—prefer **equal-area** (e.g., Equal Earth, Mollweide).  
- For a **balanced look** across metrics, try **Robinson** or **Winkel Tripel**.  
- For navigation or web-map familiarity, **Mercator** is fine—just communicate its **area exaggeration**.

> On TrueSize.World we keep projection switching minimal and instead surface the **live exaggeration by latitude** plus **overlays**. That way, anyone can see the **real size map** relationships—whether for **countries**, **continents**, **oceans**, **lakes**, **islands**, or **cities**.`
  }
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find(post => post.slug === slug);
}

export function getAllBlogPosts(): BlogPost[] {
  return blogPosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}
